
import { Component, Ref, Vue } from 'vue-property-decorator';
import { DesignAction, DesignGetter, DesignState, ModelTab, PlanTab } from '@/store/Design/DesignRoot';

// Services
import UserService from '@/utils/UserService';

// Utils
import { addHorizontalScroll } from '@/utils/Helper';

// SVG
import BriefcaseSVG from '@/assets/briefcase.svg';
import PencilSVG from '@/assets/pencil.svg';
import ClipboardCheckSVG from '@/assets/clipboard-check.svg';
import HomeSVG from '@/assets/home.svg';
import CrossSVG from '@/assets/cross.svg';
import ModelsSVG from '@/assets/models.svg';

// Components
import { VAppHeader, VAppLayout, VAppMain, VAppSidebar, VAppSidebarNavigation } from '@/components/VApp';
import VHeader from '@/components/VHeader.vue';
import VButtonIcon from '@/components/VButton/VButtonIcon.vue';
import VErrorViewer from '@/components/VErrorViewer.vue';

@Component({
    components: {
        VAppSidebarNavigation,
        VAppSidebar,
        VAppLayout,
        VAppHeader,
        VAppMain,
        VHeader,
        VButtonIcon,
        HomeSVG,
        CrossSVG,
        ModelsSVG,
        VErrorViewer,
    },
    beforeRouteLeave(to, from, next) {
        (this as DesigningPage).SELECT_HEADER_TAB(-1);
        (this as DesigningPage).SELECT_MODEL_HEADER_TAB(-1);
        next();
    },
})
export default class DesigningPage extends Vue {
    @DesignState designPageLoading!: boolean;

    @DesignState planTabs!: PlanTab[];
    @DesignState modelTabs!: PlanTab[];

    @DesignGetter currentTab?: PlanTab;
    @DesignGetter currentModelTab?: ModelTab;

    @DesignAction SELECT_HEADER_TAB!: (id: number) => void;
    @DesignAction LOAD_HEADER_TABS!: () => Promise<void>;
    @DesignAction CLOSE_HEADER_TAB!: (id: number) => void;

    @DesignAction SELECT_MODEL_HEADER_TAB!: (id: number) => void;
    @DesignAction LOAD_MODEL_HEADER_TABS!: () => Promise<void>;
    @DesignAction CLOSE_MODEL_HEADER_TAB!: (id: number) => void;

    @Ref() tabsWrapper!: HTMLElement;

    readonly linksList = [
        {
            label: this.$t('page.designing.sidebar.projects'),
            to: { name: 'designing.home.projects' },
            icon: BriefcaseSVG,
        },
        {
            label: this.$t('page.designing.sidebar.plans'),
            to: { name: 'designing.home.drawings' },
            icon: PencilSVG,
        },
        {
            label: this.$t('page.designing.sidebar.tasks'),
            to: { name: 'designing.home.tasks' },
            icon: ClipboardCheckSVG,
        },
        {
            label: this.$t('page.designing.sidebar.models'),
            to: { name: 'designing.home.models' },
            icon: ModelsSVG,
        },
    ];

    readonly planRoutes = ['designing.home', 'designing.home.drawings', 'designing.plans'];
    readonly modelRoutes = ['designing.home.models', 'designing.model'];
    readonly homeRoutes = ['designing.home.drawings', 'designing.home.models'];
    readonly sidebarRoutes = [
        'designing.home.drawings',
        'designing.home.models',
        'designing.home.tasks',
        'designing.home.projects',
    ];

    get isPlan(): boolean {
        return this.isRouteInRoutes(this.planRoutes);
    }

    get isModel(): boolean {
        return this.isRouteInRoutes(this.modelRoutes);
    }

    get onHomePage(): boolean {
        return this.isRouteInRoutes(this.homeRoutes);
    }

    get isShownSidebar(): boolean {
        return this.isRouteInRoutes(this.sidebarRoutes);
    }

    get hasUserDesignsAccess(): boolean {
        return UserService.isDesigningSubsystemUser();
    }

    get tabs() {
        if (this.isPlan) return this.planTabs;
        else if (this.isModel) return this.modelTabs;
        return [];
    }

    goHomeHandler() {
        if (this.isPlan) {
            this.SELECT_HEADER_TAB(-1);
            if (!this.onHomePage) this.$router.push({ name: 'designing.home' });
        } else if (this.isModel) {
            this.SELECT_MODEL_HEADER_TAB(-1);
            if (!this.onHomePage) this.$router.push({ name: 'designing.home.models' });
        }
    }

    selectTabHandler(id: number) {
        if (this.isPlan) {
            this.SELECT_HEADER_TAB(id);
        } else if (this.isModel) {
            this.SELECT_MODEL_HEADER_TAB(id);
        }
    }

    closeTabHandler(id: number) {
        if (this.isPlan) {
            this.CLOSE_HEADER_TAB(id);
            if (!this.planTabs.some(({ isOpen }) => isOpen)) this.goHomeHandler();
        } else if (this.isModel) {
            this.CLOSE_MODEL_HEADER_TAB(id);
            if (!this.modelTabs.some(({ isOpen }) => isOpen)) this.goHomeHandler();
        }
    }

    isRouteInRoutes(routes: string[]): boolean {
        const name = this.$route.name ?? '';

        return routes.includes(name) ? true : false;
    }

    async created() {
        if (this.isPlan) await this.LOAD_HEADER_TABS();
        if (this.isModel) await this.LOAD_MODEL_HEADER_TABS();
    }

    mounted() {
        addHorizontalScroll(this.tabsWrapper, this);
    }
}
